import React from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { getThemeColorHexValue } from '@/components/lib/theme';
import { checkIfBoostedProjection, checkIfEdgeProjection } from '@/feature/lobby/utils/lineupsUtils';
import { designSystem, withOpacity } from '@/styles/styles';
import { Projection, ProjectionType } from '@/types/api.generated';
import { AtLeast } from '@/types/utils';
import { getColorByProjType } from '@/utils/getProjectionsTypes';
import { getIconByProjType } from '@/utils/projections';

type ProjectionIconProps = {
    selectedProjection: AtLeast<Projection, 'type'>;
    style?: StyleProp<ViewStyle>;
    showLevels?: boolean;
    isVoided?: boolean;
};

/* This function determine which levels are filled based on the projection type
MiniBoosted - 1 level filled in on the bar
Boosted - 2 levels filled in
SuperBoosted - 3 levels filled in
Boosted4 - 4 levels filled in

Edge_1 - 1 level filled in
Edge_2 - 2 levels filled in
Edge_3 - 3 levelis filled in
Edge_4 - 4 levels filled in
**/
const getFilledLevelsForProjection = (type: ProjectionType): boolean[] => {
    switch (type) {
        case ProjectionType.MiniBoosted:
            return [false, false, false, true];
        case ProjectionType.Boosted:
            return [false, false, true, true];
        case ProjectionType.SuperBoosted:
            return [false, true, true, true];
        case ProjectionType.Boosted4:
            return [true, true, true, true];
        case ProjectionType.Edge1:
            return [false, false, false, true];
        case ProjectionType.Edge2:
            return [false, false, true, true];
        case ProjectionType.Edge3:
            return [false, true, true, true];
        case ProjectionType.Edge4:
            return [true, true, true, true];
        default:
            return [false, false, false, false];
    }
};

/* Display the projection icon for selected projection */
export const ProjectionIcon = ({
    selectedProjection,
    style,
    showLevels = true,
    isVoided = false,
}: ProjectionIconProps) => {
    const isNonRegularProjection = selectedProjection.type !== ProjectionType.Regular;
    const shouldShowLevelsForProjection =
        (checkIfBoostedProjection(selectedProjection.type) || checkIfEdgeProjection(selectedProjection.type)) &&
        showLevels;

    const specialIncrease =
        (selectedProjection?.nonRegularPercentage && selectedProjection?.nonRegularPercentage > 0) || false;
    const iconColor = isVoided ? 'neutral400' : getColorByProjType(selectedProjection?.type, specialIncrease);
    const bgColors = getFilledLevelsForProjection(selectedProjection.type);
    const iconColorOnBadge = isVoided ? designSystem.colors.neutral900 : ds.colorValue.icon.inverse;

    if (!isNonRegularProjection) {
        return null;
    }

    return (
        <Row>
            <Box
                style={[styles.iconWrapper, style, styles.shadowProp]}
                padding={'s2'}
                justifyContent={'center'}
                backgroundColor={iconColor}
                alignSelf={'flex-start'}
                borderRadius={'r6'}
            >
                {getIconByProjType({
                    type: selectedProjection?.type,
                    specialIncrease,
                    color: iconColorOnBadge,
                    width: 16,
                    height: 16,
                })}
            </Box>
            {shouldShowLevelsForProjection ? (
                <Box style={styles.marginLeft} gap={'s2'} marginTop={'s2'} height={16}>
                    {bgColors.map((isLevelFilled, index) => {
                        return (
                            <Box
                                key={index}
                                // we need to display 4 levels for the projection icon
                                // 14 is the width of the biggest level
                                // 2 is the difference between each level
                                width={14 - index * 2}
                                style={[
                                    styles.levelOfProjection,
                                    {
                                        backgroundColor: isLevelFilled
                                            ? getThemeColorHexValue(iconColor)
                                            : withOpacity(getThemeColorHexValue(iconColor), 0.15),
                                    },
                                ]}
                            />
                        );
                    })}
                </Box>
            ) : null}
        </Row>
    );
};

const styles = StyleSheet.create({
    iconWrapper: {
        borderCurve: 'continuous',
    },
    levelOfProjection: {
        height: 2.5,
        borderRadius: 2,
        borderCurve: 'continuous',
    },
    marginLeft: {
        marginLeft: -4,
    },
    shadowProp: {
        zIndex: 1,
        shadowColor: withOpacity(designSystem.colors.neutralBlack, 0.8),
        shadowOffset: { width: 1, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 3,
        elevation: 3,
    },
});
